<script>
import SelectWithSearch from "@/views/pages/Dilfoods/Common/SelectWithSearch.vue";
import { PackagingPath, BrandsPath, OpsPath } from "@/path";
import Swal from "sweetalert2";
import axios from "axios";

export default {
  components: { SelectWithSearch },
  props: {
    packaging: { type: Array, default: () => [] }, // list coming from parent
    option: { type: Object, required: true }, // { id, title }
  },
  data() {
    return {
      fields: ["packaging", "qty", "brand", "actions"],
      current_page: 1,
      base_url: process.env.VUE_APP_API_BACKEND_URL,

      // form
      form: {
        op_id: this.option?.id || null,
        packaging_id: null,
        brand_ids: [],
        usage: null, // create expects usage (we'll fallback to qty if needed)
      },
      selectedPackaging: null,

      // UX
      key: 1,
      saving: false,
      deletingIds: new Set(),

      // local rendering (instant updates)
      localRows: [],
      tableKey: 0,
    };
  },
  computed: {
    PackagingPath() {
      return PackagingPath;
    },
    BrandsPath() {
      return BrandsPath;
    },
    canSave() {
      return (
        !!this.form.op_id &&
        !!this.form.packaging_id &&
        this.form.usage !== null &&
        this.form.usage !== "" &&
        Number(this.form.usage) >= 0 &&
        Array.isArray(this.form.brand_ids) &&
        this.form.brand_ids.length > 0 &&
        !this.saving
      );
    },
  },
  watch: {
    // reset form when switching item
    option: {
      handler(n) {
        this.resetForm(n?.id || null);
      },
      deep: false,
    },
    // sync prop -> localRows, force table rerender
    packaging: {
      handler(newVal) {
        this.localRows = Array.isArray(newVal)
          ? JSON.parse(JSON.stringify(newVal))
          : [];
        this.tableKey += 1;
      },
      immediate: true,
      deep: true,
    },
  },
  methods: {
    resetForm(opId) {
      this.form = {
        op_id: opId,
        packaging_id: null,
        brand_ids: [],
        usage: null,
      };
      this.selectedPackaging = null;
      this.key += 1;
    },

    setPackaging(v) {
      this.form.packaging_id = v?.id || null;
      this.selectedPackaging = v || null;
      this.key += 1;
    },

    onBrandsSelected(v) {
      const arr = Array.isArray(v) ? v : [];
      this.form.brand_ids = arr.map((x) => Number(x?.id ?? x)).filter(Boolean);
    },

    async savePackaging() {
      if (
        !this.option?.id ||
        !this.form.packaging_id ||
        this.form.usage === null ||
        this.form.usage === "" ||
        !this.form.brand_ids?.length
      ) {
        Swal.fire(
          "Missing fields",
          "Select packaging, enter usage, and choose brands.",
          "warning"
        );
        return;
      }

      this.saving = true;
      const baseUrl = this.base_url + OpsPath.SAVEOPSPACKAGING;

      // primary payload (usage)
      const payload = {
        op_id: Number(this.option.id),
        packaging_id: Number(this.form.packaging_id),
        usage: Number(this.form.usage),
        brand_ids: this.form.brand_ids.map(Number),
      };

      // fallback if server expects 'qty' key name
      const fallback = {
        ...payload,
        qty: payload.usage,
      };
      delete fallback.usage;

      try {
        let res = await axios.post(baseUrl, payload, {
          headers: { "Content-Type": "application/json" },
        });
        if (!res.data?.status) {
          res = await axios.post(baseUrl, fallback, {
            headers: { "Content-Type": "application/json" },
          });
        }

        if (res.data?.status) {
          Swal.fire("Updated Successfully", "", "success");

          // accept array or single
          const apiRows = Array.isArray(res.data.data)
            ? res.data.data
            : [res.data.data];

          // normalize to table row
          const newRows = apiRows.map((m) => ({
            op_id: m.op_id,
            brand_id: m.brand_id,
            packaging_id: m.packaging_id,
            qty: m.qty ?? m.usage, // API may send qty or usage
            packaging_name: m.packaging?.name, // if API nests packaging
            brand_name: m.brand?.name, // if API nests brand
            units: m.units, // if API returns units
          }));

          // instant in-modal merge (by composite key)
          const byKey = new Map(
            this.localRows.map((r) => [
              `${r.op_id}-${r.brand_id}-${r.packaging_id}`,
              r,
            ])
          );
          newRows.forEach((n) => {
            byKey.set(`${n.op_id}-${n.brand_id}-${n.packaging_id}`, n);
          });
          this.localRows = Array.from(byKey.values());
          this.tableKey += 1;

          // emit up (optional; parent can listen if needed)
          this.$emit("packaging-updated", {
            optionId: this.option.id,
            newPackagings: newRows,
          });

          // ready for next entry
          this.resetForm(this.option.id);
        } else {
          Swal.fire(
            "Error",
            res.data?.message || "Failed to save packaging",
            "error"
          );
        }
      } catch (error) {
        console.error(error);
        Swal.fire("Error", "Failed to save packaging", "error");
      } finally {
        this.saving = false;
      }
    },

    async deleteRow(row) {
      const r = row.item;
      const payload = {
        packaging_id: r.packaging_id,
        brand_id: r.brand_id,
        op_id: r.op_id,
        usage: r.qty, // your delete endpoint expects "usage" (mirroring your existing code)
      };
      const key = `${r.op_id}-${r.brand_id}-${r.packaging_id}`;

      try {
        this.deletingIds.add(key);
        const res = await axios.post(
          this.base_url + OpsPath.DELETEOPSPACKAGING,
          payload,
          {
            headers: { "Content-Type": "application/json" },
          }
        );

        if (res.data.status) {
          Swal.fire("Deleted Successfully", "", "success");

          // remove instantly from modal
          this.localRows = this.localRows.filter(
            (x) => `${x.op_id}-${x.brand_id}-${x.packaging_id}` !== key
          );
          this.tableKey += 1;

          // emit up (optional)
          this.$emit("packaging-deleted", {
            optionId: this.option.id,
            packagingKey: key,
          });
        } else {
          Swal.fire(
            "Error",
            res.data.message || "Failed to delete packaging",
            "error"
          );
        }
      } catch (error) {
        console.log(error);
        Swal.fire("Error", "Failed to delete packaging", "error");
      } finally {
        this.deletingIds.delete(key);
      }
    },
  },
};
</script>

<template>
  <div>
    <!-- Form -->
    <div class="mb-3">
      <b-row>
        <b-col sm="4">
          <label>Packaging</label>
          <SelectWithSearch
            :search_api="PackagingPath.SEARCH_PACKAGING"
            track_by="id"
            display="name"
            :full_object="true"
            @selectedOption="setPackaging"
          />
        </b-col>
        <b-col sm="4">
          <label :key="key">Usage</label>
          <b-input v-model.number="form.usage" type="number" min="0"></b-input>
          <small
            v-if="form.usage === null || form.usage === ''"
            class="text-muted"
          >
            Enter usage
          </small>
        </b-col>
        <b-col sm="4">
          <label>Brands</label>
          <SelectWithSearch
            :search_api="BrandsPath.BRAND_SEARCH"
            track_by="id"
            display="label"
            :multi_select="true"
            @selectedOption="onBrandsSelected"
          />
          <small v-if="!form.brand_ids.length" class="text-muted"
            >Select one or more brands</small
          >
        </b-col>
      </b-row>

      <b-row class="mt-3">
        <b-col>
          <b-button
            :disabled="!canSave"
            variant="success"
            @click="savePackaging"
          >
            <b-spinner small v-if="saving" class="mr-2" /> Save
          </b-button>
          <b-button
            class="ml-2"
            variant="outline-secondary"
            @click="resetForm(option?.id)"
          >
            Clear
          </b-button>
        </b-col>
      </b-row>
    </div>

    <!-- Table -->
    <b-table
      :key="tableKey"
      :items="localRows"
      :fields="fields"
      :per-page="10"
      responsive="sm"
      :current-page="current_page"
      show-empty
      empty-text="No packagings tagged yet"
    >
      <template v-slot:cell(packaging)="row">
        {{ row.item.packaging_name }}
      </template>

      <template v-slot:cell(brand)="row">
        {{ row.item.brand_name }}
      </template>

      <template v-slot:cell(qty)="row">
        {{ row.item.qty }}
      </template>

      <template v-slot:cell(actions)="row">
        <b-button
          variant="danger"
          @click="deleteRow(row)"
          :disabled="
            deletingIds.has(
              `${row.item.op_id}-${row.item.brand_id}-${row.item.packaging_id}`
            )
          "
        >
          <i class="fa fa-trash"></i>
        </b-button>
      </template>
    </b-table>

    <!-- Pagination -->
    <div class="row">
      <div class="col">
        <div class="dataTables_paginate paging_simple_numbers float-right">
          <ul class="pagination pagination-rounded mb-0">
            <b-pagination
              v-model="current_page"
              :total-rows="localRows.length"
              :per-page="10"
            ></b-pagination>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped></style>
